import { Download, Ellipsis, EllipsisVertical, MonitorDown, Plus, Share, SquarePlus } from "lucide-react";
import type { ReactNode } from "react";
import { install, installPlatform, isStandalone, promptInstall, type InstallPlatform } from "../lib/install.js";
import { Dialog } from "./overlay.js";
import { Button } from "./ui.js";

const Key = ({ children }: { children: ReactNode }) => (
  <span className="mx-0.5 inline-flex items-center gap-1 rounded-control border border-border bg-surface px-1.5 py-0.5 align-middle text-meta font-semibold text-text">{children}</span>
);

const STEPS: Record<InstallPlatform, { title: string; steps: ReactNode[]; note?: string }> = {
  "ios-safari": {
    title: "Add Todoer to your Home Screen",
    steps: [
      <>Tap the Share button <Key><Share size={14} aria-hidden />Share</Key> in Safari's toolbar. On iPhone it's at the bottom of the screen; if you can't see it, scroll up a little.</>,
      <>Scroll down the list and tap <Key><SquarePlus size={14} aria-hidden />Add to Home Screen</Key>.</>,
      <>Tap <Key>Add</Key> in the top-right corner.</>,
      <>Open Todoer from its new Home Screen icon. It runs full screen, and you can turn on reminders in Settings → Notifications.</>,
    ],
    note: "Apple doesn't let websites show their own install button on iPhone or iPad, so this is done from Safari's Share menu.",
  },
  "ios-other": {
    title: "Add Todoer to your Home Screen",
    steps: [
      <>Tap the Share button <Key><Share size={14} aria-hidden />Share</Key>. In Chrome and Edge it's in the address bar or under <Key><Ellipsis size={14} aria-hidden /></Key>.</>,
      <>Tap <Key><SquarePlus size={14} aria-hidden />Add to Home Screen</Key>, then <Key>Add</Key>.</>,
      <>Open Todoer from its new Home Screen icon.</>,
    ],
    note: "If you don't see Add to Home Screen, open todoer.uk in Safari and follow the steps there.",
  },
  android: {
    title: "Install Todoer on your phone",
    steps: [
      <>Tap the menu button <Key><EllipsisVertical size={14} aria-hidden /></Key> at the top right of Chrome.</>,
      <>Tap <Key><Download size={14} aria-hidden />Install app</Key> (or <Key>Add to Home screen</Key>), then <Key>Install</Key>.</>,
      <>Open Todoer from your home screen or app drawer.</>,
    ],
  },
  "desktop-chromium": {
    title: "Install Todoer on this computer",
    steps: [
      <>Click the install icon <Key><MonitorDown size={14} aria-hidden /></Key> at the right-hand end of the address bar.</>,
      <>If it isn't there, open the browser menu <Key><EllipsisVertical size={14} aria-hidden /></Key> and choose <Key>Cast, save and share → Install page as app</Key> (Chrome) or <Key>Apps → Install this site as an app</Key> (Edge).</>,
      <>Click <Key>Install</Key>. Todoer opens in its own window and appears in your Start menu or Dock.</>,
    ],
  },
  "mac-safari": {
    title: "Add Todoer to your Dock",
    steps: [
      <>In Safari's menu bar choose <Key>File → Add to Dock…</Key> (or click <Key><Share size={14} aria-hidden />Share</Key> → <Key>Add to Dock</Key>).</>,
      <>Click <Key>Add</Key>. Todoer opens in its own window from the Dock.</>,
    ],
  },
  firefox: {
    title: "Install Todoer",
    steps: [
      <>Firefox on computers can't install web apps. Open todoer.uk in Chrome, Edge or Safari and use Install there.</>,
      <>On Android, Firefox can: open the menu <Key><EllipsisVertical size={14} aria-hidden /></Key> and tap <Key><Plus size={14} aria-hidden />Install</Key>.</>,
    ],
  },
  other: {
    title: "Install Todoer",
    steps: [
      <>Open todoer.uk in Safari on iPhone or iPad, or in Chrome or Edge on Android and computers.</>,
      <>Use the browser's <Key>Add to Home Screen</Key> or <Key>Install app</Key> option.</>,
    ],
  },
};

export function InstallGuide({ onClose }: { onClose: () => void }) {
  const s = install.use();
  const platform = installPlatform();
  const guide = STEPS[platform];
  const installed = isStandalone();
  return (
    <Dialog title={installed ? "Todoer is installed" : guide.title} onClose={onClose} size="md"
      footer={<Button variant={s.event ? "secondary" : "primary"} onClick={onClose}>Done</Button>}>
      {installed ? (
        <p className="text-body text-text-muted">You're using the installed app already. To install it on another device, open todoer.uk there.</p>
      ) : (
        <div className="space-y-4">
          {s.event && (
            <div className="flex items-center gap-3 rounded-card border border-border bg-brand-soft p-3">
              <MonitorDown size={20} className="shrink-0 text-brand-text" aria-hidden />
              <p className="min-w-0 flex-1 text-body">Your browser can install Todoer in one click.</p>
              <Button variant="primary" onClick={() => void promptInstall().then(onClose)}>Install</Button>
            </div>
          )}
          <ol className="space-y-3">
            {guide.steps.map((step, i) => (
              <li key={i} className="flex gap-3 text-body leading-7">
                <span className="tabular mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-surface-sunken text-meta font-semibold text-text-muted">{i + 1}</span>
                <span className="min-w-0">{step}</span>
              </li>
            ))}
          </ol>
          {guide.note && <p className="text-meta text-text-faint">{guide.note}</p>}
        </div>
      )}
    </Dialog>
  );
}
